import { Link } from "react-router-dom";
import { useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, Star, BedDouble, Ambulance, Activity, Clock, Phone,
  CalendarCheck, Quote, ShieldCheck,
} from "lucide-react";
import Reveal from "@/components/Reveal";
import SectionHeader from "@/components/SectionHeader";
import Marquee from "@/components/Marquee";
import ReviewCard from "@/components/ReviewCard";
import ServiceModal from "@/components/ServiceModal";
import { WhatsAppIcon } from "@/components/WhatsAppFloat";
import { HOSPITAL, TOP_CONDITIONS, FACILITIES, STATIC_REVIEWS, waLink } from "@/data/site";
import { SERVICE_DETAILS } from "@/data/serviceDetails";

const lineReveal = {
  hidden: { y: "115%" },
  visible: (i) => ({
    y: 0,
    transition: { delay: 0.25 + i * 0.14, duration: 0.9, ease: [0.22, 1, 0.36, 1] },
  }),
};

const TRUST_BADGES = [
  { icon: BedDouble, label: "20 Beds" },
  { icon: Ambulance, label: "24×7 Emergency" },
  { icon: Activity, label: "Respiratory ICU" },
  { icon: Star, label: "5.0★ Google Rating" },
];

export default function Home() {
  const [reviews] = useState(STATIC_REVIEWS.slice(0, 3));
  const [activeService, setActiveService] = useState(null);
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 700], [0, 140]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative flex min-h-[92svh] flex-col overflow-hidden bg-navy-950">
        <motion.div style={{ y: heroY }} className="absolute inset-0 scale-110">
          <img loading="eager" fetchPriority="high" decoding="async" src="/images/image-5.webp" alt="Shri Sai Hospital ward" className="h-full w-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950/95 via-navy-950/80 to-navy-900/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-navy-950 via-transparent to-navy-950/40" />

        <div className="relative mx-auto flex w-full max-w-7xl flex-1 items-center px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
          <div className="w-full">
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1, duration: 0.7 }}
            className="flex items-center gap-3 text-xs font-semibold tracking-[0.3em] uppercase text-teal-300 sm:text-sm"
          >
            <span className="h-px w-10 bg-teal-300/60" /> Nanded · Est. {HOSPITAL.established}
          </motion.p>

          <h1 className="mt-6 font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl">
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" variants={lineReveal} initial="hidden" animate="visible" custom={0}>
                Shri Sai Chest
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span className="block" variants={lineReveal} initial="hidden" animate="visible" custom={1}>
                and Allergy <span className="text-teal-300">Hospital</span>
              </motion.span>
            </span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.7 }}
            className="mt-5 max-w-xl text-base text-slate-300 sm:text-lg"
          >
            {HOSPITAL.subName}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.7 }}
            className="mt-6 inline-flex items-center gap-3 rounded-full bg-white/10 py-2 pl-4 pr-5 ring-1 ring-white/20 backdrop-blur-sm"
          >
            <Quote className="h-4 w-4 text-teal-300" />
            <span data-testid="hero-tagline" className="font-heading text-sm font-semibold italic tracking-wide text-white sm:text-base">
              “{HOSPITAL.tagline}”
            </span>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 0.7 }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/book-appointment"
              data-testid="hero-book-btn"
              className="group flex items-center gap-2.5 rounded-full bg-brand px-7 py-4 font-heading text-sm font-bold text-white shadow-2xl shadow-teal-900/50 transition-all duration-300 hover:bg-brand-light hover:shadow-teal-500/30"
            >
              <CalendarCheck className="h-4 w-4" />
              Book Appointment
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="flex items-center gap-2.5 rounded-full bg-whatsapp px-7 py-4 font-heading text-sm font-bold text-white shadow-2xl shadow-green-950/50 transition-all duration-300 hover:brightness-110"
            >
              <WhatsAppIcon className="h-4 w-4" />
              WhatsApp Now
            </a>
          </motion.div>
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.2, duration: 0.8 }}
          className="relative border-t border-white/10 bg-navy-950/60 backdrop-blur-md"
        >
          <div className="mx-auto grid max-w-7xl grid-cols-2 divide-x divide-white/10 px-4 sm:px-6 lg:grid-cols-4 lg:px-8">
            {TRUST_BADGES.map((b) => (
              <div key={b.label} data-testid={`trust-badge-${b.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="flex items-center justify-center gap-2.5 px-3 py-4">
                <b.icon className="h-4 w-4 shrink-0 text-teal-300" />
                <span className="text-xs font-semibold tracking-wide text-white sm:text-sm">{b.label}</span>
              </div>
            ))}
          </div>
        </motion.div>
      </section>

      <Marquee />

      {/* ABOUT SNAPSHOT */}
      <section className="mx-auto max-w-7xl px-4 pb-20 pt-12 sm:px-6 sm:pt-14 lg:px-8 lg:pb-24 lg:pt-16">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <div className="relative mb-10 sm:mb-12">
              <div className="absolute -left-4 -top-4 h-full w-full rounded-3xl border-2 border-brand/30" />
              <img loading="lazy" decoding="async"
                src="/images/image-4.webp"
                alt="Dr. Amol Shembatwad"
                data-testid="home-doctor-photo"
                className="relative aspect-[3/4] w-full rounded-3xl object-cover shadow-2xl shadow-navy-900/20"
              />
              <div className="absolute -bottom-5 left-6 rounded-2xl bg-navy px-5 py-4 text-white shadow-xl">
                <p className="font-heading text-sm font-bold">Dr. Amol Shriram Shembatwad</p>
                <p className="text-xs text-teal-300">MBBS, MD (Pulmonology)</p>
                <p className="text-xs text-teal-300">KEM Hospital, Mumbai</p>
                <p className="text-xs text-teal-300">Allergy Asthma Specialist Course (Bangalore)</p>
              </div>
            </div>
          </Reveal>
          <div>
            <SectionHeader
              number="01"
              eyebrow="About the hospital"
              title="Specialty chest & allergy care, close to home"
              description={`Established in ${HOSPITAL.established}, Shri Sai Chest and Allergy Hospital brings advanced respiratory care to Nanded — a 20-bed specialty hospital with a dedicated Respiratory ICU, centralised oxygen, 24×7 emergency care, and in-house allergy testing, diagnostics and procedures.`}
            />
            <Reveal delay={0.15}>
              <div className="mt-6 flex flex-wrap gap-2.5" data-testid="home-services-chips">
                {[
                  { key: "skin-prick", label: "Skin Prick Allergy Test" },
                  { key: "bronchoscopy", label: "Bronchoscopy" },
                  { key: "thoracoscopy", label: "Thoracoscopy" },
                  { key: "pft-fot", label: "PFT & FOT Diagnostics" },
                ].map((s) => (
                  <button
                    key={s.key}
                    type="button"
                    data-testid={`service-chip-${s.key}`}
                    onClick={() => setActiveService(s.key)}
                    className="cursor-pointer rounded-full bg-brand/10 px-4 py-2 text-xs font-bold text-brand-dark ring-1 ring-brand/25 transition-all duration-200 hover:-translate-y-0.5 hover:bg-brand hover:text-white hover:shadow-lg hover:shadow-teal-900/20 sm:text-sm"
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              <p className="mt-4 text-base leading-relaxed text-slate-600">
                Led by Dr. Amol Shembatwad — trained at KEM Hospital, Mumbai — every patient is heard, examined carefully and guided with a clear treatment plan.
              </p>
              <Link
                to="/about"
                data-testid="home-readmore-btn"
                className="mt-7 inline-flex items-center gap-2 font-heading text-sm font-bold text-brand transition-colors hover:text-brand-dark"
              >
                Read More About the Doctor <ArrowRight className="h-4 w-4" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FACILITIES PREVIEW */}
      <section className="bg-navy-50/60 py-20 lg:py-28">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-start gap-12 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <SectionHeader
                number="02"
                eyebrow="Facilities"
                title="Everything your recovery needs, under one roof"
                description="From emergency respiratory care to round-the-clock diagnostics — built for chest and allergy patients."
              />
              <Reveal delay={0.15} className="mt-8">
                <div className="overflow-hidden rounded-3xl shadow-xl shadow-navy-900/10">
                  <img loading="lazy" decoding="async" src="/images/image-2.webp" alt="Hospital ward" className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105" />
                </div>
              </Reveal>
            </div>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:col-span-3">
              {FACILITIES.map((f, i) => (
                <Reveal key={f.name} delay={i * 0.05}>
                  <div
                    data-testid={`facility-preview-${f.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="group flex h-full flex-col items-center gap-3 rounded-2xl bg-white p-5 text-center shadow-sm ring-1 ring-slate-200/70 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg hover:ring-brand/30"
                  >
                    <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-navy-50 text-navy transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                      <f.icon className="h-5 w-5" />
                    </span>
                    <span className="text-xs font-semibold leading-snug text-navy sm:text-sm">{f.name}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CONDITIONS PREVIEW */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeader
            number="03"
            eyebrow="Conditions treated"
            title="Expert care for every breath"
            description="From everyday allergies to complex lung disease — diagnosed precisely, treated personally."
          />
          <Reveal delay={0.1}>
            <Link
              to="/services"
              data-testid="home-view-all-services-btn"
              className="inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 font-heading text-sm font-bold text-white transition-colors hover:bg-navy-700"
            >
              View All Services <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-10 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {TOP_CONDITIONS.map((c, i) => (
            <Reveal key={c.en} delay={i * 0.05}>
              <Link
                to="/services"
                data-testid={`condition-card-${c.en.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="group block h-full rounded-2xl bg-white p-5 shadow-sm ring-1 ring-slate-200/70 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:ring-brand/40"
              >
                <c.icon className="h-6 w-6 text-brand" />
                <p className="mt-4 font-heading text-base font-bold text-navy">{c.en}</p>
                <p className="mt-1 font-devanagari text-sm text-slate-500">{c.mr}</p>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* OPD TIMING BAND */}
      <section className="relative overflow-hidden bg-brand py-14">
        <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full bg-white/10" />
        <div className="absolute -bottom-20 -left-10 h-56 w-56 rounded-full bg-white/10" />
        <div className="relative mx-auto flex max-w-7xl flex-wrap items-center justify-between gap-8 px-4 sm:px-6 lg:px-8">
          <Reveal className="flex items-center gap-5">
            <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/25">
              <Clock className="h-6 w-6 text-white" />
            </span>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.25em] text-teal-100">OPD — {HOSPITAL.opd.days}</p>
              <p data-testid="opd-timing-text" className="mt-1 font-heading text-xl font-bold text-white sm:text-2xl">
                {HOSPITAL.opd.morning} &nbsp;·&nbsp; {HOSPITAL.opd.evening}
              </p>
              <p className="mt-1 text-sm font-medium text-teal-50">24×7 Emergency Respiratory Care</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <a
              href={`tel:${HOSPITAL.phone}`}
              data-testid="opd-call-btn"
              className="inline-flex items-center gap-2.5 rounded-full bg-white px-7 py-4 font-heading text-sm font-bold text-navy shadow-xl transition-transform duration-300 hover:scale-105"
            >
              <Phone className="h-4 w-4" /> Call {HOSPITAL.phone}
            </a>
          </Reveal>
        </div>
      </section>

      {/* REVIEWS PREVIEW */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:px-8 lg:py-28">
        <div className="flex flex-wrap items-end justify-between gap-6">
          <SectionHeader
            number="04"
            eyebrow="Patient stories"
            title="Trusted by families across Nanded"
          />
          <Reveal delay={0.1} className="flex items-center gap-3">
            <span className="flex items-center gap-1.5 rounded-full bg-white px-4 py-2 shadow-sm ring-1 ring-slate-200">
              <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
              <span className="text-sm font-bold text-navy">5.0</span>
              <span className="text-xs text-slate-500">Google</span>
            </span>
            <Link to="/reviews" data-testid="home-all-reviews-btn" className="inline-flex items-center gap-2 font-heading text-sm font-bold text-brand hover:text-brand-dark">
              All Reviews <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {reviews.map((r, i) => (
            <Reveal key={r.id || i} delay={i * 0.08}>
              <ReviewCard review={r} testId={`home-review-card-${i}`} />
            </Reveal>
          ))}
          {!reviews.length && (
            <Reveal className="md:col-span-3">
              <div className="rounded-2xl bg-white p-8 text-center text-sm text-slate-500 ring-1 ring-slate-200">
                <ShieldCheck className="mx-auto mb-2 h-6 w-6 text-brand" /> Rated 5.0 on Google by 40 patients · 4.8 on Justdial by 56 patients
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="relative overflow-hidden bg-navy-950 py-20 lg:py-24">
        <div className="absolute inset-0 opacity-20">
          <img loading="lazy" decoding="async" src="/images/image-5.webp" alt="" className="h-full w-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950/80 to-navy-950/95" />
        <div className="relative mx-auto max-w-4xl px-4 text-center sm:px-6">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-teal-300">Don't ignore that cough</p>
            <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl lg:text-5xl text-balance">
              Book your consultation today
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-base text-slate-300">
              Breathing problems rarely fix themselves. Get a clear diagnosis and a plan from a specialist who listens.
            </p>
            <div className="mt-9 flex flex-wrap items-center justify-center gap-4">
              <a
                href={waLink()}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="cta-whatsapp-btn"
                className="flex items-center gap-2.5 rounded-full bg-whatsapp px-8 py-4 font-heading text-sm font-bold text-white shadow-2xl transition-transform duration-300 hover:scale-105"
              >
                <WhatsAppIcon className="h-4 w-4" /> WhatsApp Now
              </a>
              <a
                href={`tel:${HOSPITAL.phone}`}
                data-testid="cta-call-btn"
                className="flex items-center gap-2.5 rounded-full bg-white px-8 py-4 font-heading text-sm font-bold text-navy shadow-2xl transition-transform duration-300 hover:scale-105"
              >
                <Phone className="h-4 w-4" /> Call {HOSPITAL.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <ServiceModal
        detail={activeService ? SERVICE_DETAILS[activeService] : null}
        onClose={() => setActiveService(null)}
      />
    </div>
  );
}
